import { useState, type RefObject } from 'react';
import { IconClose } from '@belong/icons';
import { Button, Carousel, Link, Text } from '@belong/ui';
import clsx from 'clsx';
import { AddressBar } from 'src/components/address-bar/address-bar';
import { RegionSelectorButton } from 'src/components/header/region-selector-button/region-selector-button';
import { SocialProof } from 'src/features/home-page/components/social-proof/social-proof';
import { HOMEOWNERS_STRINGS as STRINGS } from '../../../../homeowners.strings';
import originalCoverCss from '../../../cover/cover.module.css';
import { IconPlus } from './IconPlus';
import css from './gttl-cover.module.css';

export enum CoverCardScrollIds {
  GuaranteedRent = 'guaranteed-rent',
  ResidentsLove = 'residents-love',
  BelovedHome = 'beloved-home',
}

export function CoverCard({
  title,
  subtitle,
  footnote,
  description,
  imageSrc,
  className,
  scrollToId,
}: {
  title: string;
  subtitle?: string;
  footnote?: string;
  description?: string;
  imageSrc: string;
  className?: string;
  scrollToId?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const hasDescription = Boolean(description);

  return (
    <div
      className={clsx(css.card, 'relative w-full', className, {
        [css.isOpen!]: isOpen,
        'cursor-pointer': hasDescription,
      })}
      onClick={() => {
        if (hasDescription) {
          setIsOpen((o) => !o);
        }
      }}
    >
      <div className={clsx(css.cardInner, 'relative size-full')}>
        <div
          className={clsx(
            css.cardFront,
            'absolute size-full rounded bg-no-repeat text-center',
          )}
          style={{
            background: `linear-gradient(146.8deg, #5F6C71 42.88%, #0C1E28 95.05%)`,
          }}
        >
          {hasDescription && (
            <IconPlus className="right-sm top-2sm h-md w-md absolute" />
          )}
          <Text className="px-xs pt-2xl lg:px-sm xl:text-h3-sm whitespace-nowrap text-sm font-semibold md:whitespace-pre-line">
            {title}
          </Text>
          {subtitle && (
            <Text
              variant="p1"
              className="px-sm max-md:mt-2xs md:mt-xs text-balance"
            >
              {subtitle}
            </Text>
          )}
          <img
            src={imageSrc}
            alt={title}
            className={clsx(
              css.cardImage,
              'absolute left-1/2 -translate-x-1/2',
              footnote ? 'bottom-md' : 'bottom-0',
            )}
          />
          {footnote && (
            <Text
              variant="p1"
              className="px-sm pb-2sm text-gray absolute inset-x-0 bottom-0 text-balance"
            >
              {footnote}
            </Text>
          )}
        </div>
        <div
          className={clsx(
            css.cardBack,
            'bg-dark-navy px-sm py-md absolute flex size-full flex-col justify-between rounded text-left',
          )}
        >
          <IconClose className="right-sm top-2sm h-md w-md absolute" />
          <Text variant="h3" className="mt-2xl">
            {description}
          </Text>
          {scrollToId && (
            <Button
              variant="text"
              buttonType="secondary"
              className="self-end"
              onClick={(e) => {
                e.preventDefault();
                const element = document.getElementById(scrollToId);
                element?.scrollIntoView({
                  behavior: 'smooth',
                });
              }}
            >
              Learn more
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

type GttlCoverContentProps = {
  phoneNumber?: string;
  city?: string;
  ctaRef?: RefObject<HTMLDivElement>;
  heroCopyVariant?: string;
};

export function GttlCoverContent({
  phoneNumber,
  city,
  ctaRef,
  heroCopyVariant,
}: GttlCoverContentProps) {
  const isV2HeroCopy = heroCopyVariant === 'test';
  const cards = [
    {
      title: STRINGS['guaranteedTimeToLoveLanding.card1'],
      description: STRINGS['guaranteedTimeToLoveLanding.card1Description'],
      imageSrc:
        'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/gttl/lp_gtr_card_bg_01.png',
      scrollToId: CoverCardScrollIds.ResidentsLove,
    },
    {
      title: STRINGS['guaranteedTimeToLoveLanding.card2'],
      subtitle: STRINGS['guaranteedTimeToLoveLanding.card2Subtitle'],
      footnote: STRINGS['guaranteedTimeToLoveLanding.card2Footnote'],
      description: STRINGS['guaranteedTimeToLoveLanding.card2Description'],
      imageSrc:
        'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/gttl/lp_gtr_card_bg_02.png',
      scrollToId: CoverCardScrollIds.GuaranteedRent,
    },
    {
      title: STRINGS['guaranteedTimeToLoveLanding.card3'],
      description: STRINGS['guaranteedTimeToLoveLanding.card3Description'],
      imageSrc:
        'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/gttl/lp_gtr_card_bg_03.png',
      // scrollToId: CoverCardScrollIds.BelovedHome,
    },
    {
      title: STRINGS['guaranteedTimeToLoveLanding.card4'],
      description: STRINGS['guaranteedTimeToLoveLanding.card4Description'],
      imageSrc:
        'https://belong-consumer-assets.s3.us-west-1.amazonaws.com/gttl/lp_gtr_card_bg_04.png',
    },
  ];

  return (
    <>
      <div className="mb-sm pt-sm z-10 flex justify-center">
        <RegionSelectorButton theme="dark" color="white" />
      </div>
      {!isV2HeroCopy && (
        <Text
          as="span"
          variant="body"
          fontWeight="semibold"
          className={clsx(
            'mb-sm bg-navy p-xs text-p1 md:text-body z-10 rounded text-center text-white',
            css.city,
          )}
        >
          {city} {STRINGS['guaranteedTimeToLoveLanding.regionText']}
        </Text>
      )}
      {isV2HeroCopy ? (
        <Text
          as="h2"
          variant="h1"
          fontWeight="semibold"
          className={clsx(
            css.heroTitle,
            'mb-sm text-h3-xl md:text-h1-md md:leading-h1-xl lg:text-h1-xl whitespace-pre-line text-center text-white',
          )}
        >
          {STRINGS['guaranteedTimeToLoveLanding.v2.title']}
        </Text>
      ) : (
        <Text
          as="h2"
          variant="h1"
          fontWeight="semibold"
          className={clsx(
            css.heroTitle,
            'mb-sm text-h3-xl md:text-h1-md md:leading-h1-xl lg:text-h1-xl text-center text-white md:whitespace-pre-line',
          )}
        >
          {STRINGS['guaranteedTimeToLoveLanding.titlePrefix']}{' '}
          <span className="text-green">
            {STRINGS['guaranteedTimeToLoveLanding.titleHighlight']}
          </span>
          <br />
          {STRINGS['guaranteedTimeToLoveLanding.titleSuffix']}
        </Text>
      )}
      <Text
        as="h3"
        variant="h3"
        className="tracking-sm w-[340px] whitespace-pre-line text-white md:w-auto md:whitespace-normal"
      >
        {isV2HeroCopy
          ? STRINGS['guaranteedTimeToLoveLanding.v2.subtitle']
          : STRINGS['guaranteedTimeToLoveLanding.subtitle']}
      </Text>
      <div
        ref={ctaRef}
        className={clsx(
          'mt-xl px-2sm md:my-2xl flex w-full flex-col items-center',
          originalCoverCss.addressRedirectContainer,
        )}
      >
        <AddressBar variant="filled" />
        {phoneNumber && (
          <div className="mt-lg flex w-full justify-center">
            <span>
              <Text fontWeight="semibold" as="span" className="text-white">
                Or Call
              </Text>{' '}
              <Link href={`tel:+1${phoneNumber}`} buttonType="secondary">
                {phoneNumber}
              </Link>
            </span>
          </div>
        )}
      </div>

      <section className="-mb-sm mt-2xl md:mb-3xl md:mt-lg flex items-center justify-center">
        <SocialProof variant="dark" />
      </section>

      <div
        className={clsx(
          css.cardsContainer,
          'gap-2sm px-sm lg:gap-lg hidden w-full max-w-[1450px] grid-cols-4 text-white md:grid',
        )}
      >
        {cards.map((card, index) => (
          <CoverCard
            key={index}
            title={card.title}
            subtitle={card.subtitle}
            footnote={card.footnote}
            description={card.description}
            imageSrc={card.imageSrc}
            scrollToId={card.scrollToId}
          />
        ))}
      </div>
      <div className={clsx(css.cardsContainer, 'flex w-full md:hidden')}>
        <div
          className={clsx(
            'carousel-container flex w-full justify-center text-white',
          )}
        >
          <Carousel blurredEnds={false} dotsWrapperClassname="-mt-5xl ">
            {cards.map((card, index) => (
              <div
                key={index}
                className="pb-5xl pt-3xl flex min-w-full justify-center"
              >
                <CoverCard
                  title={card.title}
                  subtitle={card.subtitle}
                  footnote={card.footnote}
                  description={card.description}
                  imageSrc={card.imageSrc}
                  className={css.carouselCard}
                  scrollToId={card.scrollToId}
                />
              </div>
            ))}
          </Carousel>
        </div>
      </div>
    </>
  );
}

export function GttlCover({
  ctaRef,
  heroCopyVariant,
}: Pick<GttlCoverContentProps, 'ctaRef' | 'heroCopyVariant'>) {
  const isV2HeroCopy = heroCopyVariant === 'test';

  return (
    <div
      className={clsx(
        css.hero,
        'pt-xl flex flex-col items-center bg-no-repeat text-center',
      )}
    >
      <div className={clsx(css.sun, isV2HeroCopy && 'hidden md:block')} />
      <div
        className={clsx(css.rightCloud, isV2HeroCopy && 'hidden md:block')}
      />
      <GttlCoverContent ctaRef={ctaRef} heroCopyVariant={heroCopyVariant} />
    </div>
  );
}
